import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@getsynapse/design-system';
import { SHORT_INPUTS_LENGTH } from 'utils/constants';
import { EditableTableRowCell } from '../EditableTableRowCell';
import { TeamNameEditInput } from './TeamNameEditInput';

interface TeamNameTableRowCellProps {
  value: string;
  isEditable: boolean;
  readOnlyTo: string;
  onRename: (name: string) => void;
  className?: string;
}

export const TeamNameTableRowCell = ({
  value,
  isEditable,
  readOnlyTo,
  onRename,
  className = '',
}: TeamNameTableRowCellProps) => {
  const [editValue, setEditValue] = useState(value);
  const [isEditing, setIsEditing] = useState(false);
  const saveTriggerRef = useRef<(() => void) | null>(null);
  const cancelTriggerRef = useRef<(() => void) | null>(null);
  const editTriggerRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  const handleSave = () => {
    setIsEditing(false);
    const trimmed = editValue.trim();
    if (trimmed === '' || trimmed === value.trim()) {
      setEditValue(value);
      return;
    }
    onRename(trimmed);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValue(value);
  };

  // Re-seed the editor from the authoritative `value` whenever it (re)opens.
  // A failed rename leaves `value` unchanged, so without this the editor would
  // pre-fill the previously-rejected text on the next open.
  const handleEnterEditMode = () => {
    setIsEditing(true);
    setEditValue(value);
  };

  // Only the name text / pencil affordance opens the editor. Stop propagation
  // so this click doesn't also trigger the row's navigate-to-detail handler.
  const startEdit = (event: React.MouseEvent) => {
    event.stopPropagation();
    editTriggerRef.current?.();
  };

  if (!isEditable) {
    return (
      <div className={className}>
        <Link to={readOnlyTo}>{value}</Link>
      </div>
    );
  }

  return (
    // Only swallow clicks while editing so the editor's input/buttons don't
    // navigate the row. In read mode, clicks on empty cell area bubble up to
    // the row's navigate-to-detail handler.
    <div onClick={(e) => isEditing && e.stopPropagation()}>
      <EditableTableRowCell
        isEditable
        className={className}
        disableCellClick
        editTriggerRef={editTriggerRef}
        saveTriggerRef={saveTriggerRef}
        cancelTriggerRef={cancelTriggerRef}
        showHoverInput
        readModeChildren={
          <span className='truncate cursor-pointer' onClick={startEdit}>
            {value}
          </span>
        }
        hoverInputChildren={
          <span
            className='inline-flex items-center max-w-full cursor-pointer'
            onClick={startEdit}
          >
            <span className='truncate min-w-0'>{value}</span>
            <Icon
              name='pencil-outline'
              className='ml-2 text-neutral-dark shrink-0'
              data-testid='team-name-pencil'
            />
          </span>
        }
        editModeChildren={
          <TeamNameEditInput
            value={editValue}
            onChange={setEditValue}
            maxLength={SHORT_INPUTS_LENGTH}
            onSave={() => saveTriggerRef.current?.()}
            onCancel={() => cancelTriggerRef.current?.()}
          />
        }
        onSave={handleSave}
        onCancel={handleCancel}
        onEnterEditMode={handleEnterEditMode}
      />
    </div>
  );
};
